import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { doc, getDoc } from 'firebase/firestore';

import firebase from '../../../../firebase';
import { IApiState } from '../../../../types/api';

const GLOBAL_CONFIG_COLLECTION = '9japay_global_config';
const SAVINGS_SUBCOLLECTION = 'savings';
const INTEREST_RATES_DOC = 'InterestRates';
const OTHER_RATES_DOC = 'otherRates';

const getEnvSegment = (): string =>
  process.env.REACT_APP_FIREBASE_FIRESTORE_GLOBAL_CONFIG_PATH ||
  process.env.REACT_APP_FIREBASE_FIRESTORE_USER_PATH ||
  'production';

export interface ISavingsRates {
  maxInterestRate: number;
  penaltyPercentageOnInterest: number;
  taxPercentage: number;
}

const initialState: IApiState<ISavingsRates> = {
  value: null,
  status: 'idle',
  error: '',
};

const toNumber = (input: unknown, fallback = 0): number => {
  const parsed = Number(input);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getSavingsRatesAsync = createAsyncThunk<
  ISavingsRates,
  void,
  { rejectValue: string }
>('savings/rates/get', async (_, { rejectWithValue }) => {
  try {
    const env = getEnvSegment();

    const interestRef = doc(
      firebase,
      GLOBAL_CONFIG_COLLECTION,
      env,
      SAVINGS_SUBCOLLECTION,
      INTEREST_RATES_DOC,
    );
    const otherRef = doc(
      firebase,
      GLOBAL_CONFIG_COLLECTION,
      env,
      SAVINGS_SUBCOLLECTION,
      OTHER_RATES_DOC,
    );

    const [interestSnap, otherSnap] = await Promise.all([
      getDoc(interestRef),
      getDoc(otherRef),
    ]);

    const interestData = interestSnap.exists() ? interestSnap.data() : {};
    const otherData = otherSnap.exists() ? otherSnap.data() : {};

    return {
      maxInterestRate: toNumber(interestData.maxInterestRate),
      penaltyPercentageOnInterest: toNumber(
        otherData.penaltyPercentageOnInterest,
      ),
      taxPercentage: toNumber(otherData.taxPercentage),
    };
  } catch (err) {
    return rejectWithValue(
      err instanceof Error ? err.message : 'Could not load savings rates.',
    );
  }
});

export const savingsRatesSlice = createSlice({
  name: 'savings/rates',
  initialState,
  reducers: {
    resetSavingsRates(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSavingsRatesAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getSavingsRatesAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(getSavingsRatesAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Could not load savings rates.';
      });
  },
});

export const { resetSavingsRates } = savingsRatesSlice.actions;

export default savingsRatesSlice.reducer;
